import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Link2,
  PenLine,
  PhoneCall,
  RotateCcw,
  Search,
  Send,
  Signature,
  UserRound,
  X,
} from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { absoluteApiFilePreviewUrl, api, getApiErrorMessage } from '../lib/api'
import { PageFrame, PageHeader } from '../ui/PageLayout'

const INSURER_TABS = [
  { key: 'NONLIFE', label: '손해보험' },
  { key: 'LIFE', label: '생명보험' },
]

const BADGE_DESCRIPTIONS = [
  { key: '준비중', className: 'bg-slate-200 text-slate-700', description: '프로파일 검토 또는 수동 처리가 필요한 보험사' },
  { key: 'ARS', className: 'bg-amber-100 text-amber-800', description: 'ARS 전화로 주민번호 선등록 후 자동화 진행' },
  { key: '인증', className: 'bg-rose-100 text-rose-800', description: '신분증 또는 휴대폰 본인인증으로 사람 확인 필요' },
]

const INITIAL_FORM = {
  customerName: '',
  rrnFront: '',
  rrnBack: '',
  phone: '',
}

function onlyDigits(value, max) {
  const digits = String(value || '').replace(/\D/g, '')
  return max ? digits.slice(0, max) : digits
}

function todayYmd() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function maskRrn(front, back) {
  const f = onlyDigits(front, 6)
  const b = onlyDigits(back, 7)
  if (!f && !b) return '-'
  return `${f || '______'}-${b ? `${b.slice(0, 1)}******` : '*******'}`
}

function maskPhone(phone) {
  const digits = onlyDigits(phone)
  return digits.replace(/(\d{3})\d+(\d{4})/, '$1****$2') || '-'
}

function cx(...parts) {
  return parts.filter(Boolean).join(' ')
}

function StepSection({ step, title, children, headerRight }) {
  return (
    <div className="rounded border border-slate-200 bg-white">
      <div className="flex flex-wrap items-start gap-3 border-b border-slate-200 bg-white px-4 py-2.5 sm:py-3">
        <span className="mt-1 h-8 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-brand-navy">STEP {step}</div>
          <div className="text-lg font-bold text-slate-950">{title}</div>
        </div>
        {headerRight ? <div className="ml-auto flex shrink-0 items-center gap-2">{headerRight}</div> : null}
      </div>
      <div className="space-y-4 p-4">{children}</div>
    </div>
  )
}

function getInsurerLogoSrc(org) {
  if (!org) return null
  if (org.claimBrandLogoFileId) return absoluteApiFilePreviewUrl(org.claimBrandLogoFileId)
  if (org.logoUrl) return org.logoUrl
  if (org.logoImageUrl) return org.logoImageUrl
  if (org.logoFileId) return absoluteApiFilePreviewUrl(org.logoFileId)
  if (org.fileId) return absoluteApiFilePreviewUrl(org.fileId)
  return null
}

function getInsurerInitial(name) {
  const trimmed = String(name || '').trim()
  return trimmed ? trimmed.slice(0, 1) : '?'
}

function includesAny(value, words) {
  const text = String(value || '')
  return words.some((word) => text.includes(word))
}

function inferInsurerType(row) {
  const text = `${row?.orgParentName || ''} ${row?.displayName || ''} ${row?.name || ''} ${row?.code || ''}`
  const lower = text.toLowerCase()
  if (includesAny(text, ['생명']) || lower.includes('life')) return 'LIFE'
  return 'NONLIFE'
}

function classifyRegistration(row) {
  const automation = row?.customerRegistrationAutomation || {}
  const supportStatus = automation.supportStatus || 'UNKNOWN'
  const flowType = automation.flowType || 'MANUAL_ONLY'
  const badges = []

  if (automation.requiresIdAuth || automation.requiresLoginMobileAuth || flowType === 'NEEDS_HUMAN_AUTH' || supportStatus === 'EXCLUDED') {
    badges.push('인증')
  }
  if (automation.requiresArsPrereg || supportStatus === 'ARS_PREREG_SUPPORTED' || flowType === 'ARS_PREREG_THEN_CONSENT') {
    badges.push('ARS')
  }
  if (badges.length === 0 && supportStatus !== 'SUPPORTED' && supportStatus !== 'SEMI_SUPPORTED') {
    badges.push('준비중')
  }

  return {
    supportStatus,
    flowType,
    profileCode: automation.profileCode || null,
    badges,
  }
}

function badgeClassName(badge) {
  return BADGE_DESCRIPTIONS.find((item) => item.key === badge)?.className || 'bg-slate-100 text-slate-700'
}

function isJobDone(status) {
  return ['COMPLETED', 'SUCCESS', 'SUCCEEDED', 'DONE'].includes(String(status || '').toUpperCase())
}

function jobStatusLabel(status) {
  const normalized = String(status || '').toUpperCase()
  if (isJobDone(normalized)) return '완료'
  if (normalized === 'WAITING_ARS_PREREG') return 'ARS 대기'
  if (normalized === 'WAITING_CONSENT_FAX' || normalized === 'FAX_PENDING') return '동의서 팩스'
  if (normalized === 'QUEUED') return '대기'
  if (normalized === 'RUNNING') return '진행중'
  if (normalized === 'FAILED') return '실패'
  if (normalized === 'NEEDS_HUMAN') return '확인필요'
  return status || '접수 전'
}

function jobStatusTone(status) {
  const normalized = String(status || '').toUpperCase()
  if (isJobDone(normalized)) return 'bg-emerald-100 text-emerald-800'
  if (normalized === 'WAITING_ARS_PREREG') return 'bg-amber-100 text-amber-800'
  if (normalized === 'WAITING_CONSENT_FAX' || normalized === 'FAX_PENDING') return 'bg-indigo-100 text-indigo-800'
  if (normalized === 'FAILED') return 'bg-rose-100 text-rose-800'
  if (normalized === 'RUNNING') return 'bg-sky-100 text-sky-800'
  if (normalized === 'NEEDS_HUMAN') return 'bg-rose-100 text-rose-800'
  return 'bg-slate-100 text-slate-700'
}

function dataUrlToFile(dataUrl, filename) {
  const [meta, data] = String(dataUrl || '').split(',')
  const mime = /data:(.*?);base64/.exec(meta || '')?.[1] || 'image/png'
  const binary = atob(data || '')
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return new File([bytes], filename, { type: mime })
}

export default function CustomerRegistrationWizardPage() {
  const { user } = useAuth()
  const canvasRef = useRef(null)
  const drawingRef = useRef(false)
  const [form, setForm] = useState(INITIAL_FORM)
  const [insurerMasters, setInsurerMasters] = useState([])
  const [insurerOptionsLoading, setInsurerOptionsLoading] = useState(false)
  const [insurerOptionsError, setInsurerOptionsError] = useState('')
  const [insurerTab, setInsurerTab] = useState('NONLIFE')
  const [selectedInsurers, setSelectedInsurers] = useState(() => new Set())
  const [signatureMode, setSignatureMode] = useState('REMOTE_LINK')
  const [signatureFileId, setSignatureFileId] = useState('')
  const [signatureDataUrl, setSignatureDataUrl] = useState('')
  const [signatureLink, setSignatureLink] = useState('')
  const [signatureStatus, setSignatureStatus] = useState('')
  const [signatureLoading, setSignatureLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedIntake, setSavedIntake] = useState(null)
  const [saveError, setSaveError] = useState('')
  const [arsCompletingJobId, setArsCompletingJobId] = useState(null)
  const [openStep, setOpenStep] = useState('1')

  const plannerName = user?.name || ''
  const plannerPhone = onlyDigits(user?.phoneNumber || user?.phone || '')

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  useEffect(() => {
    let ignore = false
    setInsurerOptionsLoading(true)
    setInsurerOptionsError('')
    api.get('/operator/insurers', { params: { active: true } })
      .then((res) => {
        if (ignore) return
        const payload = res?.data?.data || res?.data || {}
        const items = Array.isArray(payload) ? payload : (payload.items || [])
        setInsurerMasters(Array.isArray(items) ? items : [])
      })
      .catch((err) => {
        if (ignore) return
        setInsurerOptionsError(getApiErrorMessage(err, '보험사 목록을 불러오지 못했습니다.'))
        setInsurerMasters([])
      })
      .finally(() => {
        if (!ignore) setInsurerOptionsLoading(false)
      })
    return () => { ignore = true }
  }, [])

  const allInsurerItems = useMemo(() => {
    return insurerMasters
      .filter((row) => row?.active !== false && row?.orgId != null)
      .map((row) => {
        const type = inferInsurerType(row)
        const classification = classifyRegistration(row)
        return {
          ...row,
          key: String(row.id),
          insurerId: row.id,
          insurerOrgId: row.orgId,
          name: row.displayName || row.orgName || row.name || row.code,
          type,
          ...classification,
        }
      })
  }, [insurerMasters])

  const insurerItems = useMemo(() => {
    return allInsurerItems
      .filter((org) => (insurerTab === 'LIFE' ? org.type === 'LIFE' : org.type === 'NONLIFE'))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ko'))
  }, [allInsurerItems, insurerTab])

  const selectedList = useMemo(
    () => allInsurerItems.filter((item) => selectedInsurers.has(item.key)),
    [allInsurerItems, selectedInsurers],
  )

  const jobRows = useMemo(() => {
    const jobs = Array.isArray(savedIntake?.jobs) ? savedIntake.jobs : []
    if (jobs.length > 0) {
      return jobs.map((job) => ({
        key: `job-${job.id}`,
        id: job.id,
        insurerName: job.insurerName || job.insurerCode,
        supportStatus: job.supportStatus,
        flowType: job.flowType,
        status: job.status,
        job,
      }))
    }
    return selectedList.map((item) => ({
      key: `selected-${item.key}`,
      id: null,
      insurerName: item.name,
      supportStatus: item.supportStatus,
      flowType: item.flowType,
      status: 'PENDING_SUBMIT',
      job: null,
    }))
  }, [savedIntake, selectedList])

  const progress = useMemo(() => {
    const total = jobRows.length
    const done = jobRows.filter((row) => isJobDone(row.status)).length
    return {
      total,
      done,
      percent: total > 0 ? Math.round((done / total) * 100) : 0,
    }
  }, [jobRows])

  const formValid = useMemo(() => {
    if (!form.customerName.trim()) return false
    if (onlyDigits(form.rrnFront).length !== 6) return false
    if (onlyDigits(form.rrnBack).length !== 7) return false
    if (onlyDigits(form.phone).length < 10) return false
    if (selectedList.length < 1) return false
    return true
  }, [form, selectedList])

  const activeSignatureFileId = signatureFileId

  const buildPayload = useCallback((resolvedSignatureFileId = activeSignatureFileId) => ({
    customer: {
      name: form.customerName.trim(),
      rrn: `${onlyDigits(form.rrnFront, 6)}${onlyDigits(form.rrnBack, 7)}`,
      phone: onlyDigits(form.phone),
    },
    planner: {
      name: plannerName || null,
      phone: plannerPhone || null,
    },
    registration: {
      method: 'FAX_CONSENT',
      consentReceivedAt: todayYmd(),
      faxNumber: null,
      documentFileName: null,
      customerSignatureFileId: resolvedSignatureFileId || null,
      signatureCollectionMode: resolvedSignatureFileId ? signatureMode : signatureMode,
      insurers: selectedList.map((item) => ({
        insurerId: item.insurerId,
        insurerOrgId: item.insurerOrgId,
        orgId: item.insurerOrgId,
        code: item.code,
        name: item.name,
        supportStatus: item.supportStatus,
        flowType: item.flowType,
        profileCode: item.profileCode,
        faxNumber: item.faxNumber || null,
      })),
      memo: null,
    },
  }), [activeSignatureFileId, form.customerName, form.phone, form.rrnBack, form.rrnFront, plannerName, plannerPhone, selectedList, signatureMode])

  const toggleInsurer = (key) => {
    setSelectedInsurers((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const loadExistingSignature = async () => {
    setSignatureLoading(true)
    setSignatureStatus('')
    try {
      const params = new URLSearchParams()
      params.set('customerName', form.customerName.trim())
      params.set('phone', onlyDigits(form.phone))
      const res = await api.get(`/customer-registration/intakes/signature-candidates?${params.toString()}`)
      const payload = res?.data?.data || res?.data || {}
      const candidate = Array.isArray(payload.items) ? payload.items[0] : null
      if (!candidate?.fileId) {
        setSignatureStatus('등록된 고객 서명이 없습니다. 링크 또는 패드로 받아주세요.')
        setSignatureFileId('')
        return
      }
      setSignatureMode('REUSE_CUSTOMER_SIGNATURE')
      setSignatureFileId(candidate.fileId)
      setSignatureStatus('기존 고객 서명을 불러왔습니다.')
    } catch (err) {
      setSignatureStatus(getApiErrorMessage(err, '기존 서명을 불러오지 못했습니다.'))
    } finally {
      setSignatureLoading(false)
    }
  }

  const createSignatureLink = () => {
    const token = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
    setSignatureMode('REMOTE_LINK')
    setSignatureLink(`${window.location.origin}/sign/customer-registration/${token}`)
    setSignatureStatus('고객에게 보낼 서명 링크를 준비했습니다.')
  }

  const getCanvasPos = (event) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const point = event.touches?.[0] || event
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1
    return {
      x: (point.clientX - rect.left) * scaleX,
      y: (point.clientY - rect.top) * scaleY,
    }
  }

  const startDraw = (event) => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    const pos = getCanvasPos(event)
    drawingRef.current = true
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
  }

  const draw = (event) => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx || !drawingRef.current) return
    const pos = getCanvasPos(event)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#111827'
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
  }

  const stopDraw = () => {
    drawingRef.current = false
    const canvas = canvasRef.current
    if (!canvas) return
    try {
      setSignatureDataUrl(canvas.toDataURL('image/png'))
      setSignatureMode('IN_PERSON_PAD')
      setSignatureFileId('')
      setSignatureStatus('패드 서명이 준비되었습니다.')
    } catch {
      setSignatureStatus('패드 서명을 저장하지 못했습니다.')
    }
  }

  const clearPad = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setSignatureDataUrl('')
    setSignatureStatus('패드 서명을 지웠습니다.')
  }

  const uploadPadSignatureIfNeeded = async () => {
    if (signatureMode !== 'IN_PERSON_PAD' || !signatureDataUrl) return signatureFileId
    if (signatureFileId) return signatureFileId
    const file = dataUrlToFile(signatureDataUrl, `customer-registration-signature-${Date.now()}.png`)
    const formData = new FormData()
    formData.append('file', file)
    const res = await api.post('/files', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    const payload = res?.data?.data || res?.data || {}
    const fileId = payload.fileId || payload.id
    if (!fileId) throw new Error('SIGNATURE_UPLOAD_FAILED')
    setSignatureFileId(fileId)
    return fileId
  }

  const submitIntake = async () => {
    if (!formValid || saving) return
    setSaving(true)
    setSaveError('')
    setSavedIntake(null)
    try {
      const resolvedSignatureFileId = await uploadPadSignatureIfNeeded()
      const res = await api.post('/customer-registration/intakes', buildPayload(resolvedSignatureFileId))
      const data = res?.data?.data || res?.data
      setSavedIntake(data)
      setOpenStep('3')
    } catch (err) {
      setSaveError(getApiErrorMessage(err, '고객등록 접수 저장에 실패했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  const completeArsPrereg = async (jobId) => {
    if (!jobId || arsCompletingJobId) return
    setArsCompletingJobId(jobId)
    setSaveError('')
    try {
      const res = await api.post(`/customer-registration/intakes/jobs/${jobId}/ars-prereg/completed`)
      const updatedJob = res?.data?.data || res?.data
      setSavedIntake((prev) => {
        if (!prev || !Array.isArray(prev.jobs)) return prev
        return {
          ...prev,
          jobs: prev.jobs.map((job) => (job.id === jobId ? { ...job, ...updatedJob } : job)),
        }
      })
    } catch (err) {
      setSaveError(getApiErrorMessage(err, 'ARS 완료 처리에 실패했습니다.'))
    } finally {
      setArsCompletingJobId(null)
    }
  }

  const submitButton = (
    <button
      type="button"
      disabled={!formValid || saving}
      onClick={submitIntake}
      className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-navy px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-300"
      title={!formValid ? '고객정보와 보험사를 입력해 주세요.' : undefined}
    >
      <Send className="h-4 w-4" />
      {saving ? '저장 중...' : '고객등록 접수'}
    </button>
  )

  const nextButton = (nextStep, disabled = false) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setOpenStep(nextStep)}
      className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
    >
      다음
    </button>
  )

  return (
    <PageFrame>
      <PageHeader title="고객등록" description="고객지원" />
      <div className="mx-auto w-full max-w-6xl">

        <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {[
            ['1', '등록할 보험사선택'],
            ['2', '고객정보 입력'],
            ['3', '등록진행'],
          ].map(([step, title]) => {
            const disabled = (step === '2' && selectedList.length === 0) || (step === '3' && !formValid)
            return (
            <button
              key={step}
              type="button"
              data-testid={`customer-registration-step-${step}`}
              disabled={disabled}
              onClick={() => setOpenStep(step)}
              className={cx(
                'rounded border px-3 py-2.5 text-left transition',
                openStep === step ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white hover:bg-slate-50',
                disabled ? 'cursor-not-allowed opacity-60 hover:bg-white' : '',
              )}
            >
              <div className="text-xs font-semibold text-slate-500">STEP {step}</div>
              <div className="mt-1 text-sm font-semibold text-slate-900 sm:text-base">{title}</div>
            </button>
            )
          })}
        </div>

        <div className="space-y-4">
          {openStep === '1' && (
          <StepSection
            step="1"
            title="등록할 보험사선택"
            headerRight={(
              <>
                <div className="text-sm text-slate-500">선택 {selectedList.length}건</div>
                {nextButton('2', selectedList.length === 0)}
              </>
            )}
          >
            <div className="flex flex-wrap gap-2.5">
              {INSURER_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setInsurerTab(tab.key)}
                  className={cx(
                    'rounded border px-3 py-2 text-sm font-semibold',
                    insurerTab === tab.key
                      ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                      : 'bg-white text-slate-700 hover:bg-slate-50',
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="rounded border bg-slate-50 p-3">
              <div className="grid gap-2 text-xs text-slate-600 md:grid-cols-3">
                {BADGE_DESCRIPTIONS.map((item) => (
                  <div key={item.key} className="flex items-center gap-2">
                    <span className={cx('inline-flex shrink-0 rounded px-1.5 py-0.5 font-semibold', item.className)}>
                      {item.key}
                    </span>
                    <span>{item.description}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-4">
              {insurerOptionsLoading ? (
                <div className="text-sm text-slate-600">보험사 목록을 불러오는 중입니다.</div>
              ) : null}
              {insurerOptionsError ? (
                <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{insurerOptionsError}</div>
              ) : null}
              {insurerItems.map((item) => {
                const selected = selectedInsurers.has(item.key)
                const logoSrc = getInsurerLogoSrc(item)
                return (
                  <button
                    key={item.key}
                    type="button"
                    data-testid={`customer-registration-insurer-${item.id}`}
                    onClick={() => toggleInsurer(item.key)}
                    className={cx(
                      'rounded border px-3 py-2 text-left transition',
                      selected ? 'border-brand-navy bg-slate-50' : 'border-slate-200 bg-white hover:bg-slate-50',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex items-center gap-2">
                        {logoSrc ? (
                          <img
                            src={logoSrc}
                            alt={`${item.name} 로고`}
                            className="h-7 w-7 shrink-0 rounded border bg-white object-contain p-0.5"
                          />
                        ) : (
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded border bg-white text-xs font-semibold text-slate-600">
                            {getInsurerInitial(item.name)}
                          </span>
                        )}
                        <div className="min-w-0 flex flex-wrap items-center gap-1.5">
                          <span className="truncate text-sm font-medium text-slate-900">{item.name}</span>
                          {item.badges.map((badge) => (
                            <span key={`${item.key}-${badge}`} className={cx('inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium', badgeClassName(badge))}>
                              {badge}
                            </span>
                          ))}
                        </div>
                      </div>
                      <span className={cx(
                        'inline-flex h-5 min-w-5 items-center justify-center rounded-full border text-xs',
                        selected ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-slate-300 text-slate-400',
                      )}>
                        {selected ? '✓' : ''}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>

            {!insurerOptionsLoading && !insurerOptionsError && insurerItems.length === 0 ? (
              <div className="text-sm text-slate-600">표시할 보험사가 없습니다.</div>
            ) : null}

            <div className="rounded border bg-slate-50 p-3">
              <div className="text-sm font-semibold text-slate-900">선택된 보험사</div>
              {selectedList.length === 0 ? (
                <div className="mt-2 text-sm text-slate-600">보험사를 하나 이상 선택해 주세요.</div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2.5">
                  {selectedList.map((item) => (
                    <span key={item.key} className="inline-flex max-w-full items-center gap-1.5 rounded border bg-white py-1 pl-2 pr-0.5 text-sm">
                      {getInsurerLogoSrc(item) ? (
                        <img
                          src={getInsurerLogoSrc(item)}
                          alt={`${item.name} 로고`}
                          className="h-5 w-5 shrink-0 rounded border bg-white object-contain p-0.5"
                        />
                      ) : (
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded border bg-white text-[10px] font-semibold text-slate-600">
                          {getInsurerInitial(item.name)}
                        </span>
                      )}
                      <span className="min-w-0 truncate text-slate-900">{item.name}</span>
                      <button
                        type="button"
                        className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        aria-label={`${item.name} 선택 해제`}
                        title="선택 해제"
                        onClick={() => toggleInsurer(item.key)}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </StepSection>
          )}

          {openStep === '2' && (
          <StepSection
            step="2"
            title="고객정보 입력"
            headerRight={(
              <>
                <div className="text-sm text-slate-500">선택 {selectedList.length}건</div>
                {nextButton('3', !formValid)}
              </>
            )}
          >
            <div className="rounded border bg-slate-50 p-3">
              <div className="text-sm font-semibold text-slate-900">선택된 보험사</div>
              <div className="mt-2 flex flex-wrap gap-2.5">
                {selectedList.map((item) => (
                  <span key={item.key} className="inline-flex max-w-full items-center gap-1.5 rounded border bg-white py-1 pl-2 pr-1 text-sm">
                    {getInsurerLogoSrc(item) ? (
                      <img
                        src={getInsurerLogoSrc(item)}
                        alt={`${item.name} 로고`}
                        className="h-5 w-5 shrink-0 rounded border bg-white object-contain p-0.5"
                      />
                    ) : (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded border bg-white text-[10px] font-semibold text-slate-600">
                        {getInsurerInitial(item.name)}
                      </span>
                    )}
                    <span className="min-w-0 truncate text-slate-900">{item.name}</span>
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded border border-slate-200 bg-white p-4">
              <div className="mb-4 flex items-center gap-2">
                <UserRound className="h-5 w-5 text-sky-600" />
                <h2 className="text-base font-bold text-slate-900">고객 정보</h2>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700">고객명</span>
                  <input
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                    value={form.customerName}
                    onChange={(e) => update('customerName', e.target.value)}
                    placeholder="홍길동"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700">휴대폰 번호</span>
                  <input
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                    value={form.phone}
                    onChange={(e) => update('phone', onlyDigits(e.target.value, 11))}
                    placeholder="01012345678"
                    inputMode="numeric"
                  />
                </label>
                <div className="sm:col-span-2">
                  <span className="text-xs font-semibold text-slate-700">주민등록번호</span>
                  <div className="mt-1 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                    <input
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                      value={form.rrnFront}
                      onChange={(e) => update('rrnFront', onlyDigits(e.target.value, 6))}
                      placeholder="YYMMDD"
                      inputMode="numeric"
                    />
                    <span className="text-slate-400">-</span>
                    <input
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                      value={form.rrnBack}
                      onChange={(e) => update('rrnBack', onlyDigits(e.target.value, 7))}
                      placeholder="뒤 7자리"
                      inputMode="numeric"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <Signature className="h-5 w-5 text-sky-600" />
                <h2 className="text-base font-bold text-slate-900">고객 서명</h2>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <button
                  type="button"
                  disabled={signatureLoading || !form.customerName.trim() || onlyDigits(form.phone).length < 10}
                  onClick={loadExistingSignature}
                  className="flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <Search className="h-4 w-4" />
                  {signatureLoading ? '조회 중...' : '기존서명 불러오기'}
                </button>
                <button
                  type="button"
                  onClick={createSignatureLink}
                  className={cx(
                    'flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold',
                    signatureMode === 'REMOTE_LINK' ? 'border-sky-500 bg-sky-50 text-sky-800' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  )}
                >
                  <Link2 className="h-4 w-4" />
                  링크 생성
                </button>
                <button
                  type="button"
                  onClick={() => setSignatureMode('IN_PERSON_PAD')}
                  className={cx(
                    'flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold',
                    signatureMode === 'IN_PERSON_PAD' ? 'border-sky-500 bg-sky-50 text-sky-800' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  )}
                >
                  <PenLine className="h-4 w-4" />
                  패드 입력
                </button>
              </div>

              {signatureFileId ? (
                <div className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800">
                  서명 파일 연결됨: {signatureFileId}
                </div>
              ) : null}
              {signatureLink ? (
                <div className="mt-2 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                  {signatureLink}
                </div>
              ) : null}
              {signatureStatus ? <div className="mt-2 text-xs text-slate-600">{signatureStatus}</div> : null}

              {signatureMode === 'IN_PERSON_PAD' ? (
                <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2">
                  <canvas
                    ref={canvasRef}
                    width={560}
                    height={180}
                    className="h-36 w-full touch-none rounded border bg-white"
                    onMouseDown={startDraw}
                    onMouseMove={draw}
                    onMouseUp={stopDraw}
                    onMouseLeave={stopDraw}
                    onTouchStart={(e) => { e.preventDefault(); startDraw(e) }}
                    onTouchMove={(e) => { e.preventDefault(); draw(e) }}
                    onTouchEnd={(e) => { e.preventDefault(); stopDraw() }}
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={clearPad}
                      className="inline-flex items-center gap-1 rounded border bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      지우기
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </StepSection>
          )}

          {openStep === '3' && (
          <StepSection step="3" title="등록진행" headerRight={submitButton}>
            <div className="rounded border bg-slate-50 p-3">
              <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <div className="text-xs font-semibold text-slate-500">고객</div>
                  <div className="mt-1 font-medium text-slate-900">{form.customerName || '-'}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500">주민번호</div>
                  <div className="mt-1 font-medium text-slate-900">{maskRrn(form.rrnFront, form.rrnBack)}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500">휴대폰</div>
                  <div className="mt-1 font-medium text-slate-900">{maskPhone(form.phone)}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500">담당자</div>
                  <div className="mt-1 font-medium text-slate-900">{plannerName || '-'}</div>
                </div>
              </div>
              <div className="mt-3">
                <div className="text-xs font-semibold text-slate-500">보험사</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedList.length ? selectedList.map((item) => (
                    <span key={item.key} className="rounded bg-white px-2 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">{item.name}</span>
                  )) : <span className="text-sm text-slate-400">-</span>}
                </div>
              </div>
            </div>

            <div className="rounded border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-slate-900">전체 진행도</div>
                <div className="text-sm font-bold text-slate-900">{progress.done}/{progress.total || 0}</div>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-brand-navy transition-all"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
              <div className="mt-2 text-xs text-slate-500">{progress.percent}% 완료</div>
            </div>

            <div className="grid gap-2 md:grid-cols-2">
              {jobRows.map((row) => (
                <div key={row.key} className="rounded border border-slate-200 bg-white p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-900">{row.insurerName || '-'}</div>
                      <div className="mt-1 text-xs text-slate-500">{row.supportStatus || '-'} / {row.flowType || '-'}</div>
                    </div>
                    <span className={cx('shrink-0 rounded px-2 py-0.5 text-[11px] font-bold', jobStatusTone(row.status))}>
                      {jobStatusLabel(row.status)}
                    </span>
                  </div>
                  {row.status === 'WAITING_ARS_PREREG' ? (
                    <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                      <div>ARS 전화로 주민번호 선등록 후 완료를 눌러주세요.</div>
                      <button
                        type="button"
                        disabled={arsCompletingJobId === row.id}
                        onClick={() => completeArsPrereg(row.id)}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-amber-300"
                      >
                        <PhoneCall className="h-3.5 w-3.5" />
                        {arsCompletingJobId === row.id ? '처리 중...' : 'ARS 완료'}
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            {!formValid ? (
              <p className="text-xs text-rose-600">고객 정보와 보험사를 입력하면 접수할 수 있습니다.</p>
            ) : null}
            {saveError ? <p className="text-xs text-rose-600">{saveError}</p> : null}

            {savedIntake ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                접수 #{savedIntake.id} 저장 완료. 상태: {savedIntake.status}
              </div>
            ) : null}
          </StepSection>
          )}
        </div>
      </div>
    </PageFrame>
  )
}
